import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { GithubMark } from '../config/design';
import { ALLOWED_POOLS } from '../config/constants';
import I18nLock from './I18nLock';

const CLOSE_MS = 420;   // must match the exit-slide animation duration in CSS

const REPOS = [
  { url: 'https://github.com/Misha-btc/Acai',        label: 'Misha-btc/Acai' },
  { url: 'https://github.com/sorrow8/arbuz-child',   label: 'sorrow8/arbuz-child' },
  { url: 'https://github.com/sorrow8/arbuz',         label: 'sorrow8/arbuz' },
];

const CONTENT = {
  en: {
    repoDescs: ['Arbuz token contract', 'Card factory contract', 'Card generator contract'],
    kicker:      'FIELD GUIDE',
    title:       'How Magic Arbuz works',
    nav: [
      { n: '01', id: 'overview', label: 'Overview'  },
      { n: '02', id: 'minting',  label: 'Arbuz Mint' },
      { n: '03', id: 'deck',     label: 'Card Mint'  },
      { n: '04', id: 'fees',     label: 'Fees'       },
      { n: '05', id: 'risks',    label: 'Risks'      },
    ],
    specs: [
      ['Protocol',      'Alkanes · Bitcoin L1'],
      ['ARBUZ id',      '2 : 25349'],
      ['Card Collection id', '2 : 69849'],
      ['Card Mint window',   'Every 144 blocks (~24h)'],
      ['First window',  'Block 907,205'],
      ['Card cost',     '100 Arbuz'],
      ['Acai Contract Requirement', '1,069 sats per Arbuz mint'],
    ],
    overview: {
      lead: 'A short field guide to minting, burning, and the deck.',
      learnCta: 'Learn how Arbuzino & Wunsch work',
      tldr:       'TL;DR',
      specsTitle:  'Specifications',
      p1: <>Arbuz is a Bitcoin alkane deployed on <a href="https://x.com/RebarLabs/status/1929196559687725334" target="_blank" rel="noopener noreferrer">Acai Bitcoin Hackathon 2025 winner contract ↗</a> - minted through specific Bitcoin mining pools. Every Arbuz mint also mints <b>Diesel</b> and <b>OG Rune</b>, regardless of outcome.</>,
      p2: 'If you decide to burn 100 Arbuz when the mint window opens, the protocol returns a Magic Arbuz Card from the deck.',
      p3: 'Each card carries a unique prediction. Its art, its traits, and its prophecy are derived from a hash of the card index, on-chain.',
      p4: "The generative art isn't hosted on a server - it's JavaScript frozen inside the Bitcoin blockchain. When you open a card, the app fetches that code from the chain and runs it live in your browser. The animation and the prediction execute directly from Bitcoin.",
      steps: [
        { n: '1', title: 'MINT ARBUZ TOKEN',      body: '330 sats + 1,069 sats Acai contract requirement + network fee. Choose Rebar (private) or Public mempool.' },
        { n: '2', title: 'MINT MAGIC ARBUZ CARD', body: <>During an open window (every 144 blocks), burn 100 Arbuz. The contract validates timing via <a href="https://github.com/JinMaa/clock-in" target="_blank" rel="noopener noreferrer">clock-in ↗</a>.</> },
        { n: '3', title: 'HOLD OR SEND',          body: 'Cards are alkanes - transferable on Bitcoin L1. Each carries a unique prediction and generative art.' },
      ],
    },
    minting: {
      howTitle:  'How it works',
      howBody:   <>Each Arbuz token mint builds a PSBT with one inscription output (330 sats), one treasury output (1,069 sats Acai contract requirement), and a protostone calling <b>opcode 77</b> on the Arbuz contract (2 : 25349). On success, the contract mints <b>100 Arbuz</b> alkane to your address. Every Arbuz mint also mints <b>Diesel</b> (the alkanes genesis token) and <b>OG Rune</b> - even if the Arbuz mint fails.</>,
      routingTitle: 'Routing',
      routing: [
        { name: 'Rebar - private pool',  color: 'var(--rind)', body: <><b>Currently disabled.</b> Your signed transaction is submitted to Rebar Shield instead of the public mempool. Rebar routes it to a partner mining pool. The Rebar fee output is included in the PSBT so you pay it directly. <b>~80% success rate</b> - see Risks.</> },
        { name: 'Public - open mempool', color: 'var(--red)',  body: 'Your transaction is broadcast to the public mempool. Any miner can include it - but only approved pools will result in a successful mint. You never know whose block comes next, so the risk is much higher.' },
      ],
      outputsTitle: 'Transaction outputs',
      outputs: [
        ['Output 0', '330 sats → your taproot address (inscription)'],
        ['Output 1', 'Protostone (0 sats) - calls opcode 77'],
        ['Output 2', '1,069 sats → Acai Contract Requirement'],
        ['Output 3', 'Change → your payment address'],
        ['Output 4', 'Rebar fee (Rebar mode only)'],
      ],
      ripeKick: 'MINT STATUSES',
      ripe: [
        { tag: 'JUICY',    c: 'var(--rind)',  body: 'Mint confirmed on-chain - fresh 100 ARBUZ credited to your patch.' },
        { tag: 'RIPENING', c: 'var(--dim)',   body: 'Still in the mempool, waiting for an approved pool to confirm it.' },
        { tag: 'SPOILED',  c: 'var(--red)',   body: "Mined by an unapproved pool - the mint reverted. The fee is not refunded." },
      ],
    },
    deck: {
      genTitle:  'Card generation',
      genBody:   <>Each card's traits are derived from a <b>SHA-256 hash</b> of its index. The hash determines background, card type, main symbol, three mystical symbols, border colour, glow colour and your daily prediction. The full rendering JS is stored on-chain and fetched via <b>opcode 1000</b> on contract (2 : 69849).</>,
      typesTitle: 'Card types',
      types: [
        { name: 'Classic',  chance: 'majority',  body: '22 major arcana - Star, Moon, Sun, Tower, Wheel, Hermit, Magician, Priestess, Emperor, Empress, Devil, Fool, Hierophant, Lovers, Chariot, Strength, Justice, Hanged Man, Death, Temperance, Judgement, World.' },
        { name: 'Glitch',   chance: '~13 / 256', body: '8 special cards referencing the alkanes ecosystem - Airhead, Mist, Puppet, Taco, Acai, Diesel, Clockin, Cheekyb. Always gold border and glow.' },
        { name: 'Absolute', chance: '< 1 / 256', body: 'Ultra-rare. Only two possible: Fartane (silver) or Arbuz (green). Determined by hash byte 25 < 1.' },
      ],
      windowTitle: 'Mint window',
      windowBody:  <>The card contract calls <b>clock-in</b> (opcode 103) before minting. Windows open every <b>144 blocks</b> starting from block 907,205. Miss the window - the transaction fails and you keep your Arbuz token.</>,
      predTitle: 'Prediction',
      predBody:  'Every card carries a unique prediction - derived from the same on-chain hash that generates the art. Click the card to toggle the prediction between English and Chinese.',
    },
    fees: {
      arbuzTitle: 'Minting ARBUZ',
      arbuzRows: [
        ['Inscription output', '330 sats (to your address)'],
        ['Acai Contract Requirement', '1,069 sats (every mint)'],
        ['Network fee',        '~251 vB × fee rate'],
        ['Rebar fee',          '~251 vB × Rebar rate (Rebar mode only)'],
        ['Total (public)',     '1,399 sats + network fee'],
      ],
      cardTitle: 'Minting a card',
      cardRows: [
        ['Inscription output', '330 sats (to your address)'],
        ['ARBUZ alkane',       '100 Arbuz burned (held by contract)'],
        ['Network fee',        '~300 vB × fee rate'],
        ['Total',              '330 sats + 100 Arbuz + network fee'],
      ],
    },
    risks: {
      poolsTitle: 'Allowed mining pools for Arbuz Token',
      poolsBody:  'The Acai contract validates the mining pool that includes the transaction without using 3rd parties - it\'s a pure smart contract function that can\'t be influenced by anyone since the contract is already deployed on-chain. Only blocks mined by approved pools succeed. Other pools fail - you lose the fee you sent.',
      publicTitle: 'Public mint success rate',
      publicBody:  'Although you can submit a public mint for just a few sats, your chances of losing are much higher - you never know whose block comes next. Any unsupported pool can mine the next block and your mint fails.',
      rebarTitle: 'Rebar success rate',
      rebarBody:  '~20% of blocks are still mined by unsupported pools. If your transaction lands in an unsupported block it fails. The fee is still paid.',
      timingTitle: 'Card mint timing',
      timingBody:  'If the transaction is included outside the 144-block window, the mint fails - you keep the Arbuz alkane. Submit just before the window opens with a fee rate high enough to land in the target block.',
      utxoTitle: 'Mixed UTXOs',
      utxoBody:  'If the selected UTXO holds other assets, the contract returns them automatically - only 100 ARBUZ is consumed.',
    },
  },
  zh: {
    repoDescs: ['Arbuz 代币合约', '卡牌工厂合约', '卡牌生成器合约'],
    kicker:      '使用指南',
    title:       'Magic Arbuz 工作原理',
    nav: [
      { n: '01', id: 'overview', label: '概述'  },
      { n: '02', id: 'minting',  label: 'Arbuz 铸造' },
      { n: '03', id: 'deck',     label: '卡片铸造'   },
      { n: '04', id: 'fees',     label: '费用'   },
      { n: '05', id: 'risks',    label: '风险'   },
    ],
    specs: [
      ['协议',     'Alkanes · 比特币 L1'],
      ['ARBUZ id', '2 : 25349'],
      ['集合 id',  '2 : 69849'],
      ['铸造窗口', '每 144 个区块（~24小时）'],
      ['首个窗口', '第 907,205 区块'],
      ['卡片成本', '100 Arbuz'],
      ['Acai 合约要求', '每次铸造 1,069 sats'],
    ],
    overview: {
      lead: '关于铸造、销毁与卡组的简明指南。',
      learnCta: '了解 Arbuzino 与 Wunsch 的运作方式',
      tldr:        'TL;DR',
      specsTitle:  '规格参数',
      p1: <>Arbuz 是部署在 <a href="https://x.com/RebarLabs/status/1929196559687725334" target="_blank" rel="noopener noreferrer">Acai Bitcoin Hackathon 2025 获奖合约 ↗</a> 的比特币 alkane - 通过特定比特币矿池铸造。每次 Arbuz 铸造还会铸造 <b>Diesel</b> 和 <b>OG Rune</b>，无论结果如何。</>,
      p2: '如果你决定在铸造窗口开启时销毁 100 枚 Arbuz，协议将从卡组中返回一张 Magic Arbuz 卡。',
      p3: '每张卡都带有独特预言。其艺术、特征和预言均来源于链上卡片索引的哈希。',
      p4: '生成艺术并非托管于服务器 - 它是冻结在比特币区块链中的 JavaScript。当你打开一张卡时，应用从链上获取代码并在浏览器中实时运行。动画与预言直接从比特币上执行。',
      steps: [
        { n: '1', title: '铸造 ARBUZ 代币',       body: '330 sats + 1,069 sats Acai 合约要求 + 网络费。选择 Rebar（私有）或公开内存池。' },
        { n: '2', title: '铸造 MAGIC ARBUZ 卡',   body: <>在开放窗口期间（每 144 个区块），销毁 100 枚 Arbuz。合约通过 <a href="https://github.com/JinMaa/clock-in" target="_blank" rel="noopener noreferrer">clock-in ↗</a> 验证时机。</> },
        { n: '3', title: '持有或发送',             body: '卡片是 alkane - 可在比特币 L1 上转移。每张卡都带有独特预言和生成艺术。' },
      ],
    },
    minting: {
      howTitle: '工作原理',
      howBody:  <>每次 Arbuz 代币铸造构建一个 PSBT，包含一个铭文输出（330 sats）、一个国库输出（1,069 sats Acai 合约要求），以及调用 Arbuz 合约 (2 : 25349) <b>opcode 77</b> 的 protostone。成功时，合约将 <b>100 Arbuz</b> alkane 铸造到你的地址。每次 Arbuz 铸造还会铸造 <b>Diesel</b> 和 <b>OG Rune</b> - 即使 Arbuz 铸造失败也如此。</>,
      routingTitle: '路由',
      routing: [
        { name: 'Rebar - 私有矿池',  color: 'var(--rind)', body: <><b>当前已禁用。</b>已签名交易提交到 Rebar Shield 而非公开内存池。Rebar 将其路由到合作矿池。Rebar 费用输出已包含在 PSBT 中，由你直接支付。<b>成功率约 80%</b> - 参见风险。</> },
        { name: '公开 - 开放内存池', color: 'var(--red)',  body: '你的交易被广播到公开内存池。任何矿工都可以打包 - 但只有经批准的矿池才能成功铸造。你永远不知道下一个区块是谁挖出的，因此风险要高得多。' },
      ],
      outputsTitle: '交易输出',
      outputs: [
        ['输出 0', '330 sats → 你的 taproot 地址（铭文）'],
        ['输出 1', 'Protostone（0 sats）- 调用 opcode 77'],
        ['输出 2', '1,069 sats → Acai 合约要求'],
        ['输出 3', '找零 → 你的支付地址'],
        ['输出 4', 'Rebar 费用（仅 Rebar 模式）'],
      ],
      ripeKick: '铸造状态',
      ripe: [
        { tag: 'JUICY',    c: 'var(--rind)',  body: '铸造已在链上确认 - 全新 100 ARBUZ 已记入你的账户。' },
        { tag: 'RIPENING', c: 'var(--dim)',   body: '仍在内存池中，等待经批准的矿池确认。' },
        { tag: 'SPOILED',  c: 'var(--red)',   body: '被未批准的矿池挖出 - 铸造被回滚。手续费不予退还。' },
      ],
    },
    deck: {
      genTitle: '卡片生成',
      genBody:  <>每张卡的特征来源于其索引的 <b>SHA-256 哈希</b>。哈希决定背景、卡片类型、主符号、三个神秘符号、边框颜色、发光颜色和你的每日预言。完整渲染 JS 存储在链上，通过合约 (2 : 69849) 的 <b>opcode 1000</b> 获取。</>,
      typesTitle: '卡片类型',
      types: [
        { name: '经典',  chance: '大多数',     body: '22 张大阿卡纳 - 星星、月亮、太阳、塔、命运之轮、隐者、魔术师、女祭司、皇帝、女皇、恶魔、愚者、教皇、恋人、战车、力量、正义、倒吊人、死神、节制、审判、世界。' },
        { name: '故障',  chance: '~13 / 256', body: '8 张引用 alkanes 生态系统的特殊卡 - Airhead、Mist、Puppet、Taco、Acai、Diesel、Clockin、Cheekyb。始终为金色边框和发光。' },
        { name: '绝对',  chance: '< 1 / 256', body: '极为稀有。只有两种可能：Fartane（银色）或 Arbuz（绿色）。由哈希字节 25 < 1 决定。' },
      ],
      windowTitle: '铸造窗口',
      windowBody:  <>卡片合约在铸造前调用 <b>clock-in</b>（opcode 103）。窗口从第 907,205 区块开始，每 <b>144 个区块</b>开启一次。错过窗口 - 交易失败，你保留 Arbuz 代币。</>,
      predTitle: '预言',
      predBody:  '每张卡都带有独特预言 - 来源于生成艺术的同一链上哈希。点击卡片可在中英文之间切换预言。',
    },
    fees: {
      arbuzTitle: '铸造 ARBUZ',
      arbuzRows: [
        ['铭文输出',    '330 sats（到你的地址）'],
        ['Acai 合约要求', '1,069 sats（每次铸造）'],
        ['网络费',      '~251 vB × 费率'],
        ['Rebar 费',    '~251 vB × Rebar 费率（仅 Rebar 模式）'],
        ['总计（公开）', '1,399 sats + 网络费'],
      ],
      cardTitle: '铸造卡片',
      cardRows: [
        ['铭文输出',    '330 sats（到你的地址）'],
        ['ARBUZ alkane', '100 Arbuz 销毁（由合约持有）'],
        ['网络费',      '~300 vB × 费率'],
        ['总计',        '330 sats + 100 Arbuz + 网络费'],
      ],
    },
    risks: {
      poolsTitle: 'Arbuz Token 允许的矿池',
      poolsBody:  'Acai 合约在不依赖第三方的情况下验证打包交易的矿池 - 这是一个纯智能合约函数，任何人都无法干预。只有经批准矿池挖出的区块才会成功。其他矿池会失败 - 你损失已发送的手续费。',
      publicTitle: 'Public 铸造成功率',
      publicBody:  '虽然你可以仅花几个 sats 提交公开铸造，但失败的概率要高得多 - 你永远不知道下一个区块是谁挖出的。任何不受支持的矿池都可能挖出下一个区块，导致你的铸造失败。',
      rebarTitle: 'Rebar 成功率',
      rebarBody:  '约 20% 的区块仍由不受支持的矿池挖出。如果你的交易落在不受支持的区块中则失败。费用仍需支付。',
      timingTitle: '卡片铸造时机',
      timingBody:  '如果交易在 144 区块窗口外被打包，铸造失败 - 你保留 Arbuz alkane。在窗口开启前提交，费率要足够高以确保落在目标区块。',
      utxoTitle: '混合 UTXO',
      utxoBody:  '若所选 UTXO 包含其他资产，合约会自动将其退还 - 仅消耗 100 ARBUZ。',
    },
  },
};

// ─── highlighted panel (cream block) ──────────────────────────
function Panel({ children }) {
  return (
    <div className="rm-panel">
      <div className="rm-pc">{children}</div>
    </div>
  );
}

// ─── row helper ───────────────────────────────────────────────
function RmRows({ rows }) {
  return (
    <div style={{ marginTop: 12 }}>
      {rows.map(([k, v]) => (
        <div className="rm-row" key={k}><span className="rk">{k}</span><span className="rv">{v}</span></div>
      ))}
    </div>
  );
}

// ─── sections ─────────────────────────────────────────────────
function Overview({ c, onLearn }) {
  const ov = c.overview;
  return (
    <>
      <p className="rm-lead" style={{ marginTop: 18 }}>{ov.lead}</p>
      {onLearn && (
        <button type="button" className="rm-learn-fart" onClick={onLearn}>
          <I18nLock en={CONTENT.en.overview.learnCta}>{ov.learnCta}</I18nLock><span className="rlf-arrow">→</span>
        </button>
      )}
      <Panel>
        <div className="rm-h">{ov.tldr}</div>
        <p className="rm-p lead">{ov.p1}</p>
        <p className="rm-p">{ov.p2}</p>
        <p className="rm-p">{ov.p3}</p>
        <p className="rm-p">{ov.p4}</p>
      </Panel>
      <div className="rm-3">
        {ov.steps.map((s, i) => (
          <div className="rm-step" key={s.n}>
            <div className="rm-num">{s.n}</div>
            <div className="rm-st-t">
              <I18nLock block en={CONTENT.en.overview.steps[i].title}>{s.title}</I18nLock>
            </div>
            <p>{s.body}</p>
          </div>
        ))}
      </div>
      <div className="rm-block">
        <div className="rm-h">{ov.specsTitle}</div>
        <RmRows rows={c.specs} />
      </div>
      <div className="rm-repos">
        {REPOS.map((r, i) => (
          <a className="rm-repo" key={r.url} href={r.url} target="_blank" rel="noopener noreferrer">
            <span className="rl" style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><GithubMark size={14} /> {r.label}</span>
            <span className="rd">{c.repoDescs[i]}</span>
            <span className="ra">↗</span>
          </a>
        ))}
      </div>
    </>
  );
}

function Minting({ c }) {
  const m = c.minting;
  return (
    <>
      <Panel>
        <div className="rm-h">{m.howTitle}</div>
        <p className="rm-p">{m.howBody}</p>
      </Panel>
      <div className="rm-block rm-routing">
        <div className="rm-h">{m.routingTitle}</div>
        {m.routing.map(r => (
          <div className="rm-route" key={r.name}>
            <div className="rn">{r.name}</div>
            <div className="rb">{r.body}</div>
          </div>
        ))}
      </div>
      <div className="rm-block">
        <div className="rm-h">{m.outputsTitle}</div>
        <RmRows rows={m.outputs} />
      </div>
      <div className="rm-ripe">
        <div className="rm-kick mono" style={{ marginBottom: 10 }}>{m.ripeKick}</div>
        {m.ripe.map(r => (
          <div className="rm-ripe-row" key={r.tag}>
            <span className="rm-tag" style={{ '--c': r.c }}><span className="rm-dot"></span>{r.tag}</span>
            <span>{r.body}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function Deck({ c }) {
  const d = c.deck;
  return (
    <>
      <Panel>
        <div className="rm-h">{d.genTitle}</div>
        <p className="rm-p">{d.genBody}</p>
      </Panel>
      <div className="rm-block">
        <div className="rm-h">{d.typesTitle}</div>
        <div style={{ marginTop: 6 }}>
          {d.types.map(t => (
            <div className="rm-type" key={t.name}>
              <div className="th"><span className="tnm">{t.name}</span><span className="tch">{t.chance}</span></div>
              <div className="tb">{t.body}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="rm-block">
        <div className="rm-h">{d.windowTitle}</div>
        <p className="rm-p">{d.windowBody}</p>
      </div>
      <div className="rm-block">
        <div className="rm-h">{d.predTitle}</div>
        <p className="rm-p">{d.predBody}</p>
      </div>
    </>
  );
}

function Fees({ c }) {
  const f = c.fees;
  return (
    <>
      <div className="rm-block">
        <div className="rm-h">{f.arbuzTitle}</div>
        <RmRows rows={f.arbuzRows} />
      </div>
      <div className="rm-block">
        <div className="rm-h">{f.cardTitle}</div>
        <RmRows rows={f.cardRows} />
      </div>
    </>
  );
}

function Risks({ c }) {
  const r = c.risks;
  return (
    <>
      <div className="rm-block">
        <div className="rm-h">{r.poolsTitle}</div>
        <p className="rm-p">{r.poolsBody}</p>
        <div className="rm-pools">
          {ALLOWED_POOLS.map(p => <span className="rm-pool" key={p}>{p}</span>)}
        </div>
      </div>
      <div className="rm-block"><div className="rm-h">{r.publicTitle}</div><p className="rm-p">{r.publicBody}</p></div>
      <div className="rm-block"><div className="rm-h">{r.rebarTitle}</div><p className="rm-p">{r.rebarBody}</p></div>
      <div className="rm-block"><div className="rm-h">{r.timingTitle}</div><p className="rm-p">{r.timingBody}</p></div>
      <div className="rm-block"><div className="rm-h">{r.utxoTitle}</div><p className="rm-p">{r.utxoBody}</p></div>
    </>
  );
}

const SECTION_COMPONENTS = { overview: Overview, minting: Minting, deck: Deck, fees: Fees, risks: Risks };

// ─── modal shell (drawer from the right) ──────────────────────
function ReadMeModal({ open, onClose }) {
  // ReadMe has its OWN language (synced from the global one), so switching it
  // here does NOT translate the rest of the page - matches master.
  const { lang: globalLang } = useLang();
  const [lang, setLang] = useState(globalLang);
  useEffect(() => { setLang(globalLang); }, [globalLang]);
  const [activeId, setActiveId] = useState('overview');
  const [closing, setClosing] = useState(false);
  const navigate = useNavigate();
  const goArbuzino = useCallback(() => { onClose(); navigate('/?rules=1'); }, [onClose, navigate]);
  const innerRef = useRef(null);

  // play exit slide, then unmount via parent onClose
  const requestClose = useCallback(() => { setClosing(true); setTimeout(onClose, CLOSE_MS); }, [onClose]);

  // reset to first tab + scroll top each open
  useEffect(() => {
    if (open) {
      setActiveId('overview');
      setClosing(false);
      if (innerRef.current) innerRef.current.scrollTop = 0;
    }
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, requestClose]);

  // lock background page scroll while the drawer is open (incl. exit animation).
  // Lock BOTH <html> and <body> - the document's scrolling element here is <html>,
  // so locking body alone leaves the blurred page scrollable.
  useEffect(() => {
    if (!open) return;
    const html = document.documentElement;
    const prevHtml = html.style.overflow;
    const prevBody = document.body.style.overflow;
    html.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => { html.style.overflow = prevHtml; document.body.style.overflow = prevBody; };
  }, [open]);

  if (!open) return null;

  const c = CONTENT[lang] || CONTENT.en;
  const Content = SECTION_COMPONENTS[activeId];

  const selectTab = (id) => {
    setActiveId(id);
    if (innerRef.current) innerRef.current.scrollTop = 0;
  };

  return (
    <div
      className={`modal-scrim show${closing ? ' closing' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget) requestClose(); }}
    >
      <div className="rm-card" role="dialog" aria-modal="true" aria-label="How it works">
        <div className="rm-inner" ref={innerRef}>
          <div className="rm-head">
            <div>
              <div className="rm-kick mono">{c.kicker}</div>
              <h2 className="rm-title">{c.title}</h2>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {/* one button, not a toggle — a binary EN/中文 choice is just a flip
                  (shared .lang-btn style with the top bar) */}
              <button
                className="lang-btn"
                aria-label={lang === 'en' ? 'Switch to 中文' : 'Switch to English'}
                onClick={() => setLang(lang === 'en' ? 'zh' : 'en')}
              ><I18nLock ghosts={['中文', 'EN']}>{lang === 'en' ? '中文' : 'EN'}</I18nLock></button>
              <button className="rm-x" aria-label="Close" onClick={requestClose}>×</button>
            </div>
          </div>

          {/* segmented control — white track, the selected section is an ink pill
              (the highlight slides between tabs; no motion under reduced-motion) */}
          <div role="tablist" className="rm-tabs">
            <div className="rm-tab-hl" style={{
              width: `calc((100% - 8px) / ${c.nav.length})`,
              left: `calc(4px + (100% - 8px) / ${c.nav.length} * ${c.nav.findIndex(a => a.id === activeId)})`,
            }} />
            {c.nav.map((a, i) => (
              <div
                key={a.id}
                role="tab"
                aria-selected={activeId === a.id}
                onClick={() => selectTab(a.id)}
                className={`rm-tab${activeId === a.id ? ' on' : ''}`}
              >
                <I18nLock en={CONTENT.en.nav[i].label}>{a.label}</I18nLock>
              </div>
            ))}
          </div>

          <div className="rm-body">
            <div className="rm-sec on" key={activeId}>
              <Content c={c} onLearn={activeId === 'overview' ? goArbuzino : undefined} />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default ReadMeModal;
